import type { Editor } from '@tiptap/core'

export const OUTSTANDING_CALLOUT_MARK_NAMES = [
  'notSpecified',
  'manualInstruction',
  'insufficientInformation',
] as const

export type OutstandingCalloutMarkName =
  (typeof OUTSTANDING_CALLOUT_MARK_NAMES)[number]

export type OutstandingCalloutRange = {
  sectionId: string
  from: number
  to: number
}

type SectionWithOrder = {
  sectionId: string
  sortOrder: number
}

function textNodeHasOutstandingMark(
  marks: ReadonlyArray<{ type: { name: string } }>
): boolean {
  return marks.some((mark) =>
    OUTSTANDING_CALLOUT_MARK_NAMES.includes(
      mark.type.name as OutstandingCalloutMarkName
    )
  )
}

export function collectOutstandingCalloutRanges<
  T extends SectionWithOrder,
>({
  sections,
  getEditorForSection,
  shouldIncludeSection,
}: {
  sections: T[]
  getEditorForSection: (sectionId: string) => Editor | null
  shouldIncludeSection?: (section: T) => boolean
}): OutstandingCalloutRange[] {
  const sortedSections = [...sections].sort(
    (left, right) => left.sortOrder - right.sortOrder
  )
  const hits: OutstandingCalloutRange[] = []

  for (const section of sortedSections) {
    if (shouldIncludeSection != null && !shouldIncludeSection(section)) {
      continue
    }

    const editor = getEditorForSection(section.sectionId)
    if (editor == null || editor.isDestroyed) {
      continue
    }

    editor.state.doc.descendants((node, pos) => {
      if (!node.isText || !textNodeHasOutstandingMark(node.marks)) {
        return
      }

      hits.push({
        sectionId: section.sectionId,
        from: pos,
        to: pos + node.nodeSize,
      })
    })
  }

  return hits
}

export function areOutstandingCalloutRangesEqual(
  left: OutstandingCalloutRange[],
  right: OutstandingCalloutRange[]
): boolean {
  if (left.length !== right.length) {
    return false
  }

  return left.every((leftHit, index) => {
    const rightHit = right[index]
    if (rightHit == null) {
      return false
    }
    return (
      leftHit.sectionId === rightHit.sectionId &&
      leftHit.from === rightHit.from &&
      leftHit.to === rightHit.to
    )
  })
}
